import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Calendar, Sparkles, PenSquare, Loader2, Hash, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { TiltCard } from '../components/home/TiltCard';
import { ScrollDepthReveal } from '../components/home/ScrollDepthReveal';

const BlogPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blogs`);
        setBlogs(response.ok ? await response.json() : []);
      } catch (error) {
        console.error('Error fetching blogs:', error);
        setBlogs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, [API_URL]);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const renderTile = (post, featured = false, index = 0) => (
    <ScrollDepthReveal key={post.blog_id} delay={featured ? 0 : Math.min(index * 0.06, 0.3)}>
      <Link to={`/blog/${post.slug}`} data-testid={`blog-tile-${post.slug}`} className="group block h-full">
        <TiltCard maxTilt={featured ? 6 : 12} hoverScale={featured ? 1.015 : 1.05} testId={`blog-tile-tilt-${post.slug}`} className="h-full">
          <div className="h-full bg-white/[0.04] rounded-2xl overflow-hidden shadow-lg shadow-black/20 hover:shadow-2xl hover:shadow-black/40 transition-all duration-500 border border-white/10 group-hover:border-white/30">
            <div className={`relative overflow-hidden ${featured ? 'h-72 lg:h-80' : 'h-56'}`}>
              <img src={post.featured_image} alt={post.featured_image_alt || post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <span className="absolute bottom-3 left-3 px-3 py-1 bg-white text-black text-xs font-semibold rounded-full shadow-md">
                {post.category}
              </span>
            </div>
            <div className="p-6">
              <div className="flex items-center text-neutral-500 text-xs mb-3">
                <Calendar size={12} className="mr-1.5 text-neutral-400" />
                {formatDate(post.created_at)}
                <span className="mx-2 text-neutral-600">|</span>
                <Clock size={12} className="mr-1.5 text-neutral-400" />
                {post.read_time}
              </div>
              <h3 className={`font-bold text-white mb-3 line-clamp-2 group-hover:text-neutral-300 transition-colors leading-snug ${featured ? 'text-2xl' : 'text-lg'}`} style={{ fontFamily: "'Playfair Display', serif" }}>
                {post.title}
              </h3>
              {post.hashtags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {post.hashtags.slice(0, 4).map((tag) => (
                    <span key={tag} className="inline-flex items-center px-2.5 py-1 bg-white/10 text-neutral-300 text-xs font-medium rounded-full">
                      <Hash size={10} className="mr-0.5" />{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TiltCard>
      </Link>
    </ScrollDepthReveal>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0c]">
      <Header />
      <main className="pt-20">
        <section className="relative py-16 lg:py-24 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/[0.05] rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/[0.04] rounded-full blur-3xl" />

          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-flex items-center px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full text-neutral-300 text-sm font-medium mb-6 shadow-sm">
              <Sparkles size={14} className="mr-2" />Insights & Stories
            </span>
            <h1 className="text-4xl lg:text-6xl font-bold bg-gradient-to-r from-white via-neutral-300 to-neutral-500 bg-clip-text text-transparent mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Blog
            </h1>
            <p className="text-lg text-neutral-400 max-w-2xl mx-auto leading-relaxed">
              Thoughts, stories, and insights on business, technology, and life's adventures
            </p>
            {user && (
              <Link to="/blog/new" data-testid="write-blog-cta-btn" className="inline-flex items-center mt-6 px-6 py-3 bg-white text-black font-semibold rounded-full hover:bg-neutral-200 hover:shadow-lg hover:shadow-white/20 transition-all">
                <PenSquare size={18} className="mr-2" />Write a Blog
              </Link>
            )}
          </div>
        </section>

        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-neutral-400" />
              </div>
            ) : blogs.length === 0 ? (
              <div data-testid="blog-empty-state" className="text-center py-20">
                <p className="text-neutral-500 text-lg">No blog posts yet. Check back soon!</p>
              </div>
            ) : (
              <>
                <div className="mb-12">{renderTile(blogs[0], true)}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {blogs.slice(1).map((post, i) => renderTile(post, false, i))}
                </div>
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPage;
